
import { GameState, ArtistPersonality, ArtistAction, GameEvent, MultiplayerChallenge } from '@/types/career-sim';

// Personality archetypes
export const artistPersonalities: ArtistPersonality[] = [
  {
    id: 'hustler',
    type: 'hustler',
    name: 'The Hustler',
    description: 'Always grinding, you\'re focused on consistency and hard work. You can outwork anyone, but risk burning out if you don\'t pace yourself.',
    strengths: ['High productivity', 'Good at networking', 'Consistent output'],
    weaknesses: ['Prone to burnout', 'May sacrifice quality for quantity', 'Struggles with work-life balance'],
    idealPath: 'Build a steady career through consistent output and smart networking.',
    avatar: '/assets/avatars/hustler.png',
    startingStats: {
      energy: 80,
      mentalHealth: 70,
      fanbase: 200,
      funds: 1000,
      reputation: 30,
      creativeEnergy: 60
    },
    traits: {
      productivity: 90,
      creativity: 60,
      performance: 70,
      business: 80,
      marketing: 75,
      resilience: 85
    },
    animationStyle: {
      idle: 'hustler-idle',
      working: 'hustler-working',
      performing: 'hustler-performing',
      networking: 'hustler-networking',
      exhausted: 'hustler-exhausted'
    }
  },
  {
    id: 'innovator',
    type: 'innovator',
    name: 'The Innovator',
    description: 'You\'re ahead of your time, pushing boundaries with your experimental sound. You create trends rather than follow them.',
    strengths: ['High creativity', 'Visionary ideas', 'Critical acclaim'],
    weaknesses: ['Limited mainstream appeal', 'Inconsistent income', 'Creative blocks'],
    idealPath: 'Build a dedicated following through innovative music, then leverage critical acclaim for larger opportunities.',
    avatar: '/assets/avatars/innovator.png',
    startingStats: {
      energy: 70,
      mentalHealth: 75,
      fanbase: 150,
      funds: 800,
      reputation: 40,
      creativeEnergy: 95
    },
    traits: {
      productivity: 65,
      creativity: 95,
      performance: 75,
      business: 50,
      marketing: 60,
      resilience: 70
    },
    animationStyle: {
      idle: 'innovator-idle',
      working: 'innovator-working',
      performing: 'innovator-performing',
      networking: 'innovator-networking',
      exhausted: 'innovator-exhausted'
    }
  },
  {
    id: 'purist',
    type: 'purist',
    name: 'The Purist',
    description: 'You\'re all about the music, with little interest in the business side. Your work has depth and authenticity but may need help with promotion.',
    strengths: ['Exceptional musical quality', 'Artistic integrity', 'Dedicated core fans'],
    weaknesses: ['Marketing aversion', 'Business naivety', 'Limited commercial appeal'],
    idealPath: 'Create exceptional music and find a business partner or manager to handle the promotional side.',
    avatar: '/assets/avatars/purist.png',
    startingStats: {
      energy: 75,
      mentalHealth: 85,
      fanbase: 120,
      funds: 500,
      reputation: 35,
      creativeEnergy: 90
    },
    traits: {
      productivity: 70,
      creativity: 90,
      performance: 75,
      business: 40,
      marketing: 30,
      resilience: 60
    },
    animationStyle: {
      idle: 'purist-idle',
      working: 'purist-working',
      performing: 'purist-performing',
      networking: 'purist-networking',
      exhausted: 'purist-exhausted'
    }
  },
  {
    id: 'showstopper',
    type: 'showstopper',
    name: 'The Showstopper',
    description: 'Born to perform, you shine brightest on stage. Your live shows are your biggest asset, but recording might not be your strong suit.',
    strengths: ['Electrifying performances', 'Strong stage presence', 'Fan loyalty'],
    weaknesses: ['Studio anxiety', 'Recording challenges', 'Inconsistent releases'],
    idealPath: 'Build your reputation through phenomenal live shows, then leverage that audience for recording opportunities.',
    avatar: '/assets/avatars/showstopper.png',
    startingStats: {
      energy: 85,
      mentalHealth: 80,
      fanbase: 250,
      funds: 1200,
      reputation: 45,
      creativeEnergy: 70
    },
    traits: {
      productivity: 60,
      creativity: 70,
      performance: 95,
      business: 55,
      marketing: 65,
      resilience: 75
    },
    animationStyle: {
      idle: 'showstopper-idle',
      working: 'showstopper-working',
      performing: 'showstopper-performing',
      networking: 'showstopper-networking',
      exhausted: 'showstopper-exhausted'
    }
  },
  {
    id: 'trendsetter',
    type: 'trendsetter',
    name: 'The Trendsetter',
    description: 'A social media genius who knows how to create viral moments. Your marketing savvy sets you apart, but substance matters too.',
    strengths: ['Social media expertise', 'Viral marketing', 'Cultural relevance'],
    weaknesses: ['Perceived as style over substance', 'Trend dependency', 'Creative criticism'],
    idealPath: 'Use your marketing genius to build a platform, then showcase your artistic depth to gain respect.',
    avatar: '/assets/avatars/trendsetter.png',
    startingStats: {
      energy: 75,
      mentalHealth: 70,
      fanbase: 500,
      funds: 1500,
      reputation: 25,
      creativeEnergy: 65
    },
    traits: {
      productivity: 70,
      creativity: 65,
      performance: 70,
      business: 75,
      marketing: 95,
      resilience: 60
    },
    animationStyle: {
      idle: 'trendsetter-idle',
      working: 'trendsetter-working',
      performing: 'trendsetter-performing',
      networking: 'trendsetter-networking',
      exhausted: 'trendsetter-exhausted'
    }
  },
  {
    id: 'underground',
    type: 'underground',
    name: 'The Underground Icon',
    description: 'Respected in the scene with a devoted cult following. You prioritize authenticity over commercial success.',
    strengths: ['Scene credibility', 'Devoted superfans', 'Artistic freedom'],
    weaknesses: ['Limited mainstream reach', 'Financial constraints', 'Industry skepticism'],
    idealPath: 'Maintain your underground credibility while selectively partnering with supportive mainstream platforms.',
    avatar: '/assets/avatars/underground.png',
    startingStats: {
      energy: 70,
      mentalHealth: 75,
      fanbase: 180,
      funds: 600,
      reputation: 50,
      creativeEnergy: 85
    },
    traits: {
      productivity: 75,
      creativity: 85,
      performance: 80,
      business: 45,
      marketing: 50,
      resilience: 80
    },
    animationStyle: {
      idle: 'underground-idle',
      working: 'underground-working',
      performing: 'underground-performing',
      networking: 'underground-networking',
      exhausted: 'underground-exhausted'
    }
  },
  {
    id: 'industry',
    type: 'industry',
    name: 'The Industry Player',
    description: 'You know how the music business works and how to make the right connections. Your network is your net worth.',
    strengths: ['Industry connections', 'Business acumen', 'Strategic career moves'],
    weaknesses: ['Perceived industry dependency', 'Creative authenticity questions', 'Fan skepticism'],
    idealPath: 'Leverage industry knowledge for smart partnerships while maintaining creative control and authenticity.',
    avatar: '/assets/avatars/industry.png',
    startingStats: {
      energy: 75,
      mentalHealth: 70,
      fanbase: 300,
      funds: 2000,
      reputation: 40,
      creativeEnergy: 60
    },
    traits: {
      productivity: 70,
      creativity: 60,
      performance: 65,
      business: 90,
      marketing: 80,
      resilience: 75
    },
    animationStyle: {
      idle: 'industry-idle',
      working: 'industry-working',
      performing: 'industry-performing',
      networking: 'industry-networking',
      exhausted: 'industry-exhausted'
    }
  }
];

// Artist actions
export const artistActions: ArtistAction[] = [
  {
    id: 'create-music',
    name: 'Create Music',
    description: 'Work on a new track in your studio',
    icon: 'music', 
    energyCost: 20,
    fundsCost: 0,
    location: 'bedroom-studio',
    outcomes: {
      creativeEnergy: -15,
      mentalHealth: 5,
      skills: {
        production: 1
      }
    },
    animation: 'create-music',
    duration: 3,
    cooldown: 0
  },
  {
    id: 'record-track',
    name: 'Record Track',
    description: 'Record your music at a studio',
    icon: 'mic',
    energyCost: 30,
    fundsCost: 200,
    location: 'professional-studio',
    requirements: {
      minLevel: 2,
      minFunds: 200
    },
    outcomes: {
      energy: -30,
      funds: -200,
      creativeEnergy: -25,
      skills: {
        production: 2
      }
    },
    animation: 'record-track',
    duration: 4,
    cooldown: 1
  },
  {
    id: 'perform-live',
    name: 'Perform Live',
    description: 'Play a live show to gain fans',
    icon: 'music',
    energyCost: 40,
    fundsCost: 100,
    location: 'local-venue',
    requirements: {
      minLevel: 1,
      minFunds: 100
    },
    outcomes: {
      energy: -40,
      funds: 150,
      fanbase: 20,
      reputation: 5,
      skills: {
        performance: 2
      }
    },
    animation: 'perform-live',
    duration: 5,
    cooldown: 2
  },
  {
    id: 'social-media',
    name: 'Social Media',
    description: 'Engage with fans online',
    icon: 'users',
    energyCost: 10,
    fundsCost: 0,
    location: 'social-media',
    outcomes: {
      energy: -10,
      fanbase: 8,
      skills: {
        marketing: 1
      }
    },
    animation: 'social-media',
    duration: 2,
    cooldown: 0
  },
  {
    id: 'network',
    name: 'Industry Networking',
    description: 'Meet key people in the music industry',
    icon: 'handshake',
    energyCost: 20,
    fundsCost: 50,
    location: 'record-label',
    outcomes: {
      energy: -20,
      funds: -50,
      reputation: 10,
      skills: {
        networking: 2,
        business: 1
      }
    },
    animation: 'network',
    duration: 3,
    cooldown: 1
  },
  {
    id: 'release-music',
    name: 'Release Music',
    description: 'Put your music out to the world',
    icon: 'upload',
    energyCost: 15,
    fundsCost: 100,
    location: 'bedroom-studio',
    requirements: {
      minLevel: 2,
      minFunds: 100
    },
    outcomes: {
      energy: -15,
      funds: -100,
      fanbase: 30,
      reputation: 8
    },
    animation: 'release-music',
    duration: 3,
    cooldown: 4
  },
  {
    id: 'rest',
    name: 'Rest & Recover',
    description: 'Take time to recharge your energy',
    icon: 'bed',
    energyCost: -50, // Negative means it restores energy
    fundsCost: 0,
    location: 'bedroom-studio',
    outcomes: {
      energy: 50,
      mentalHealth: 15,
      creativeEnergy: 20
    },
    animation: 'rest',
    duration: 4,
    cooldown: 0
  },
  {
    id: 'upgrade-studio',
    name: 'Upgrade Studio',
    description: 'Improve your recording equipment',
    icon: 'wrench',
    energyCost: 10,
    fundsCost: 500,
    location: 'bedroom-studio',
    requirements: {
      minLevel: 3,
      minFunds: 500
    },
    outcomes: {
      energy: -10,
      funds: -500,
      skills: {
        production: 3
      }
    },
    animation: 'upgrade-studio',
    duration: 3,
    cooldown: 7
  }
];

// Game events
export const gameEvents: GameEvent[] = [
  {
    id: 'label-offer',
    title: 'Record Label Offer',
    description: 'A record label has approached you with a contract offer. What will you do?',
    type: 'opportunity',
    choices: [
      {
        id: 'accept-deal',
        text: 'Accept the deal for immediate funding and support',
        outcomes: {
          funds: 2000,
          reputation: 15,
          fanbase: 50
        },
        personalityBonus: ['industry']
      },
      {
        id: 'negotiate',
        text: 'Try to negotiate better terms',
        outcomes: {
          reputation: 5,
          skills: {
            business: 2
          }
        },
        successChance: 60,
        personalityBonus: ['hustler', 'industry']
      },
      {
        id: 'decline',
        text: 'Stay independent and maintain creative control',
        outcomes: {
          creativeEnergy: 20,
          mentalHealth: 10
        },
        personalityBonus: ['purist', 'underground']
      }
    ]
  },
  {
    id: 'viral-opportunity',
    title: 'Viral Challenge Opportunity',
    description: 'There\'s a trending sound challenge that\'s perfect for one of your tracks. How do you approach it?',
    type: 'opportunity',
    choices: [
      {
        id: 'embrace-trend',
        text: 'Jump on the trend with a catchy version',
        outcomes: {
          fanbase: 100,
          funds: 300,
          energy: -10
        },
        personalityBonus: ['trendsetter', 'hustler']
      },
      {
        id: 'authentic-spin',
        text: 'Put your unique artistic spin on the trend',
        outcomes: {
          fanbase: 30,
          reputation: 20,
          creativeEnergy: -15
        },
        personalityBonus: ['innovator', 'purist']
      },
      {
        id: 'ignore-trend',
        text: 'Ignore the trend and focus on your own path',
        outcomes: {
          mentalHealth: 5,
          creativeEnergy: 10
        },
        personalityBonus: ['underground', 'purist']
      }
    ]
  },
  {
    id: 'burnout-risk',
    title: 'Signs of Burnout',
    description: 'You\'re feeling exhausted and unmotivated after pushing yourself too hard. How do you handle it?',
    type: 'challenge',
    choices: [
      {
        id: 'push-through',
        text: 'Push through and keep working',
        outcomes: {
          energy: -30,
          mentalHealth: -20,
          funds: 100
        }
      },
      {
        id: 'take-break',
        text: 'Take a short break to recharge',
        outcomes: {
          energy: 30,
          mentalHealth: 15,
          creativeEnergy: 10,
          funds: -50
        },
        personalityBonus: ['purist', 'innovator']
      },
      {
        id: 'creative-retreat',
        text: 'Go on a creative retreat for inspiration',
        outcomes: {
          energy: 50,
          mentalHealth: 30,
          creativeEnergy: 25,
          funds: -200
        },
        personalityBonus: ['innovator']
      }
    ]
  },
  {
    id: 'collaboration-offer',
    title: 'Collaboration Opportunity',
    description: 'A well-known artist has reached out for a potential collaboration. How do you proceed?',
    type: 'opportunity',
    choices: [
      {
        id: 'accept-collab',
        text: 'Accept the collaboration enthusiastically',
        outcomes: {
          reputation: 25,
          fanbase: 75,
          energy: -20
        },
        personalityBonus: ['industry', 'trendsetter']
      },
      {
        id: 'selective-collab',
        text: 'Only collaborate if your artistic visions align',
        outcomes: {
          reputation: 15,
          fanbase: 30,
          creativeEnergy: -10
        },
        personalityBonus: ['purist', 'innovator']
      },
      {
        id: 'propose-alternate',
        text: 'Propose an alternative collaborative approach',
        outcomes: {
          reputation: 10,
          skills: {
            business: 1,
            networking: 2
          }
        },
        personalityBonus: ['underground', 'showstopper']
      }
    ]
  },
  {
    id: 'creative-block',
    title: 'Creative Block',
    description: 'You\'ve been staring at a blank session for hours. The creative well feels dry.',
    type: 'challenge',
    choices: [
      {
        id: 'force-creativity',
        text: 'Force yourself to finish something, anything',
        outcomes: {
          creativeEnergy: -15,
          mentalHealth: -10,
          skills: {
            production: 1
          }
        },
        personalityBonus: ['hustler']
      },
      {
        id: 'seek-inspiration',
        text: 'Step away and seek inspiration elsewhere',
        outcomes: {
          creativeEnergy: 20,
          energy: -10
        },
        personalityBonus: ['innovator', 'purist']
      },
      {
        id: 'collaborate',
        text: 'Call a fellow musician to collaborate',
        outcomes: {
          creativeEnergy: 15,
          reputation: 5,
          funds: -50
        },
        personalityBonus: ['showstopper', 'industry']
      }
    ]
  }
];

// Initial multiplayer challenges
export const initialMultiplayerChallenges: MultiplayerChallenge[] = [
  {
    id: 'hit-battle-1',
    type: 'battle',
    status: 'pending',
    category: 'hit-battle',
    description: 'Create a track that will outperform your opponent\'s in streams and fan reactions.',
    startDate: new Date(),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
    rewards: {
      xp: 100,
      funds: 500,
      fanbase: 50,
      reputation: 20
    }
  },
  {
    id: 'performance-battle-1',
    type: 'battle',
    status: 'pending',
    category: 'performance',
    description: 'Battle another artist in a live performance showdown.',
    startDate: new Date(),
    endDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 days from now
    rewards: {
      xp: 75,
      funds: 300,
      fanbase: 40,
      reputation: 25
    }
  },
  {
    id: 'collab-project-1',
    type: 'collaboration',
    status: 'pending',
    category: 'collaboration',
    description: 'Team up with another artist to create a collaborative EP.',
    startDate: new Date(),
    endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 14 days from now
    rewards: {
      xp: 150,
      funds: 800,
      fanbase: 100,
      reputation: 40
    }
  }
];

// Initial game state
export const initialGameState: GameState = {
  initialized: false,
  artistPersonality: null,
  career: {
    artistLevel: 1,
    careerStage: 'bedroom',
    daysActive: 0,
    energy: 100,
    mentalHealth: 100,
    fanbase: 0,
    funds: 500,
    reputation: 10,
    creativeEnergy: 100,
    totalReleases: 0,
    skills: {
      production: 10,
      performance: 10,
      marketing: 10,
      business: 10,
      networking: 10
    },
    releases: [],
    gigs: [],
    achievements: []
  },
  environment: {
    currentLocation: 'bedroom-studio',
    studioQuality: 1,
    availableVenues: ['bedroom-studio', 'local-venue'],
    time: {
      hour: 9,
      day: 1,
      week: 1
    },
    weather: 'sunny'
  },
  inventory: {
    equipment: [
      {
        id: 'basic-computer',
        name: 'Basic Computer',
        quality: 1,
        effect: {
          productionBoost: 5
        }
      },
      {
        id: 'starter-mic',
        name: 'Starter Microphone',
        quality: 1,
        effect: {
          productionBoost: 3
        }
      }
    ],
    clothes: [
      {
        id: 'casual-outfit',
        name: 'Casual Outfit',
        style: 'casual',
        effect: {
          performanceBoost: 0
        }
      }
    ],
    items: [
      {
        id: 'energy-drink',
        name: 'Energy Drink',
        quantity: 3,
        effect: 'Restores 15 energy'
      }
    ]
  },
  actions: artistActions,
  events: gameEvents,
  challenges: [
    {
      id: 'first-fans',
      title: 'First 100 Fans',
      description: 'Build your initial fanbase to 100 followers',
      type: 'career',
      difficulty: 'easy',
      rewards: [
        {
          type: 'reputation',
          value: 5
        },
        {
          type: 'funds',
          value: 100
        }
      ],
      progress: 0,
      progressTarget: 100,
      inProgress: false,
      completed: false
    },
    {
      id: 'first-release',
      title: 'Release Your First Track',
      description: 'Create and release your first song to the world',
      type: 'career',
      difficulty: 'easy',
      rewards: [
        {
          type: 'fans',
          value: 50
        },
        {
          type: 'skill',
          value: 5,
          skillType: 'production'
        }
      ],
      inProgress: false,
      completed: false
    },
    {
      id: 'local-gig',
      title: 'First Local Show',
      description: 'Perform your first live show at a local venue',
      type: 'career',
      difficulty: 'easy',
      requirements: {
        minFans: 50
      },
      rewards: [
        {
          type: 'fans',
          value: 30
        },
        {
          type: 'skill',
          value: 3,
          skillType: 'performance'
        },
        {
          type: 'funds',
          value: 150
        }
      ],
      inProgress: false,
      completed: false
    }
  ],
  currentMood: 'energetic',
  activeAction: null,
  activeEvent: null,
  multiplayerChallenges: initialMultiplayerChallenges,
  notifications: [],
  needs: {
    energy: 100,
    mentalHealth: 100,
    physicalHealth: 100,
    creativity: 100,
    social: 100,
    finances: 100
  },
  lastSaved: null,
  currentWeek: 1,
  weeklyGoals: null,
  careerPhase: 'bedroom',
  actionCount: 0,
  unlockedActions: ['create-music', 'rest', 'social-media'],
  seenEvents: []
};
